import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AccordionSection
 *
 * mode='display'     : 기존 읽기 전용 (check_circle 아이콘, itemCheck 기반)
 * mode='interactive' : 입회 모드 - 체크 가능, checks/onCheck props 필요
 * locked             : true면 interactive 모드에서도 클릭 비활성화 + 반투명
 */
function AccordionSection({
  title,
  icon,
  isOpen,
  onToggle,
  data,
  labelKey,
  mode = 'display',
  locked = false,
  checks = {},
  onCheck,
}) {
  return (
    <div className="space-y-3">
      <div
        onClick={onToggle}
        className="group flex cursor-pointer items-center justify-between px-1"
      >
        <div className="flex items-center gap-2">
          <span
            className="material-symbols-outlined text-[#5ABD4F]"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            {icon}
          </span>
          <h3 className="font-headline text-lg font-bold text-[#191c1e]">
            {title}
          </h3>
        </div>
        <span
          className={`material-symbols-outlined text-[#becab7] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          expand_more
        </span>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-2 overflow-hidden"
          >
            {data?.length > 0 ? (
              data.map((item, i) => {
                // 입회 모드 (체크 가능)
                if (mode === 'interactive') {
                  const isChecked = checks[i] ?? false;
                  return (
                    <div
                      key={i}
                      onClick={() => !locked && onCheck?.(i, !isChecked)}
                      className={`flex items-center justify-between rounded-2xl border p-4 shadow-sm transition-all ${
                        isChecked
                          ? 'border-[#5ABD4F]/20 bg-white'
                          : 'border-slate-100 bg-slate-50/50'
                      } ${
                        locked
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer active:scale-[0.99]'
                      }`}
                    >
                      <p
                        className={`text-sm font-bold ${
                          isChecked ? 'text-[#191c1e]' : 'text-slate-400'
                        }`}
                      >
                        {item[labelKey]}
                      </p>
                      <span
                        className={`material-symbols-outlined font-bold transition-colors ${
                          isChecked ? 'text-[#5ABD4F]' : 'text-slate-300'
                        }`}
                        style={{
                          fontVariationSettings: isChecked
                            ? "'FILL' 1"
                            : "'FILL' 0",
                        }}
                      >
                        {isChecked ? 'check_circle' : 'radio_button_unchecked'}
                      </span>
                    </div>
                  );
                }

                const isChecked = item.itemCheck === '0';
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between rounded-2xl border p-4 shadow-sm transition-all ${
                      isChecked
                        ? 'border-[#5ABD4F]/20 bg-white'
                        : 'border-slate-100 bg-slate-50/50 opacity-60'
                    }`}
                  >
                    <p
                      className={`text-sm font-bold ${
                        isChecked ? 'text-[#191c1e]' : 'text-slate-400'
                      }`}
                    >
                      {item[labelKey]}
                    </p>
                    <span
                      className={`material-symbols-outlined font-bold ${
                        isChecked ? 'text-[#5ABD4F]' : 'text-slate-300'
                      }`}
                    >
                      {isChecked ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="rounded-2xl border border-dashed border-[#becab7]/30 bg-white py-4 text-center text-sm text-[#3f4a3b]/40 italic">
                선택된 항목이 없습니다.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default React.memo(AccordionSection);
